import { Box, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { getDashboardActivity } from '@/lib/api/dashboard';

const TYPE_LABEL: Record<string, string> = {
  TYPE_CHANGED: '구분 변경',
  ROOM_CHANGED: '호실 이동',
};

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('ko-KR', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** 최근 입소자 변경 이력 — 교대 근무자 인수인계용 */
export function RecentActivityCard() {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['dashboard', 'activity'],
    queryFn: () => getDashboardActivity(8),
    staleTime: 60 * 1000,
  });

  const items = query.data ?? [];

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          최근 변경 이력
        </Typography>

        {query.isError && (
          <Typography variant="body2" color="error.main">
            이력을 불러오지 못했습니다.
          </Typography>
        )}

        {!query.isLoading && !query.isError && items.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            최근 변경된 내역이 없습니다.
          </Typography>
        )}

        <Stack divider={<Divider flexItem />}>
          {items.map((a, i) => (
            <Box
              key={`${a.residentId}-${a.occurredAt}-${i}`}
              role="button"
              tabIndex={0}
              onClick={() =>
                a.residentId && navigate(`/residents?detail=${a.residentId}&tab=today`)
              }
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && a.residentId) {
                  e.preventDefault();
                  navigate(`/residents?detail=${a.residentId}&tab=today`);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Chip
                size="small"
                variant="outlined"
                label={TYPE_LABEL[a.type] ?? a.type}
                sx={{ minWidth: 78 }}
              />
              <Typography variant="body2" sx={{ minWidth: 0, flex: 1 }} noWrap>
                {a.roomNumber ? `${a.roomNumber}호 ` : ''}
                {a.name ?? '(미지정)'} · {a.summary}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                {formatWhen(a.occurredAt)}
              </Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
